import { FraudLabel, FraudParty, FraudReviewer, FraudShares, FraudStatus } from '../services/fraudService';

// Friendly text per signal code from the nightly review; unknown codes show as the code itself.
export const FRAUD_SIGNAL_LABELS: Record<string, string> = {
    IMPOSSIBLE_SPEED: 'Distance impossible for the trip time',
    INSTANT_TRIP: 'Trip ended within a minute',
    START_FAR_FROM_PICKUP: 'Started far from the booked pickup',
    WENT_ELSEWHERE: 'Ended far from the booked drop',
    BILLED_FAR_ABOVE_QUOTE: 'Billed far above the quote',
    REPEAT_PAIR: 'Same driver and rider repeatedly',
    INSTANT_STREAK: 'Driver has many instant trips',
    SELF_RIDE: 'Driver rode on their own account',
    RIDER_IS_DRIVER: 'Rider number belongs to a driver',
    RIDER_IS_STAFF: 'Rider number belongs to a staff member',
    AGENT_RIDER_CYCLING: 'Agent reused a few rider numbers',
    AGENT_DRIVER_PAIRING: "Agent's bookings keep going to one driver",
    AGENT_FLAGGED_RATE: "Many of this agent's bookings are flagged",
    END_POINT_NOT_BELIEVED: 'Trip end point not believable (GPS)',
    RIDER_ON_TWO_RIDES: 'Same rider number on two rides at once',
    NO_MOVEMENT: 'Trip never left the pickup',
    SAME_TRIP_REBOOKED: 'Same trip booked again after a short ride',
    LONG_OPEN_TRIP: 'Ride left open far too long',
};

export const fraudSignalLabel = (code: string): string =>
    Object.prototype.hasOwnProperty.call(FRAUD_SIGNAL_LABELS, code) ? FRAUD_SIGNAL_LABELS[code] : code;

/** Who could be responsible, in display order, with the share field each one reads. */
export const FRAUD_PARTIES: { key: FraudParty; label: string; share: keyof Omit<FraudShares, 'fraudPct'> }[] = [
    { key: 'DRIVER', label: 'Driver', share: 'driverPct' },
    { key: 'AGENT', label: 'Agent', share: 'agentPct' },
    { key: 'RIDER', label: 'Rider', share: 'riderPct' },
];

/** "Driver 72% · Agent 53% · Rider 20%" — a party with no share (no agent on the ride) is left out. */
export const partySplit = (shares: FraudShares): string =>
    FRAUD_PARTIES.filter((p) => typeof shares[p.share] === 'number')
        .map((p) => `${p.label} ${shares[p.share]}%`)
        .join(' · ');

/** "93% · High" */
export const fraudLikelihood = (fraudPct: number, certainty: string): string => `${fraudPct}% · ${certainty}`;

export const fraudPctColor = (pct: number): 'error' | 'warning' | 'default' =>
    pct >= 80 ? 'error' : pct >= 50 ? 'warning' : 'default';

/** "Driver, Agent" from the stored DRIVER / AGENT / RIDER verdict. */
export const responsibleLabel = (parties: string[]): string =>
    parties.map((p) => FRAUD_PARTIES.find((x) => x.key === p)?.label ?? p).join(', ');

/** Tooltip text for a ride-list chip: likelihood, the party split, then each signal's friendly name. */
export const fraudSummary = (label: FraudLabel): string =>
    [
        fraudLikelihood(label.fraudPct, label.explanation.certainty),
        partySplit(label),
        label.signals.map((s) => fraudSignalLabel(s.code)).join(', '),
    ].filter(Boolean).join(' — ');

export const FRAUD_STATUS_LABELS: Record<FraudStatus, string> = {
    OPEN: 'Potential fraud',
    CONFIRMED: 'Fraud confirmed',
    CLEARED: 'Cleared',
};

export const FRAUD_NOTE_MIN = 3;
export const FRAUD_NOTE_MAX = 500;

/** Clearing or confirming needs a note of 3+ characters; re-opening does not. */
export const isValidFraudNote = (status: FraudStatus, note: string): boolean =>
    status === 'OPEN' || note.trim().length >= FRAUD_NOTE_MIN;

export const fraudReviewerName = (reviewer?: FraudReviewer | null): string => {
    if (!reviewer) return '-';
    return [reviewer.firstName, reviewer.lastName].filter(Boolean).join(' ') || `Staff #${reviewer.id}`;
};

const BOOKING_SOURCE_LABELS: Record<string, string> = { app: 'App', qr_web: 'QR', agent: 'Agent' };

export const bookingSourceLabel = (source?: string | null): string =>
    source ? BOOKING_SOURCE_LABELS[source] ?? source : '-';

export const vehicleTypeLabel = (type?: string | null): string => (type ? type.replace(/_/g, ' ') : '-');

export const formatFare = (fare?: number | null): string => (fare == null ? '-' : `₹${fare}`);

export const formatDateTime = (iso?: string | null): string => (iso ? new Date(iso).toLocaleString() : '-');
